// utility.js

import Utility from '~/server/utils/utils';

export const defaultNumberLocales = {
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE',
  it: 'it-IT',
  pt: 'pt-PT',
  ru: 'ru-RU',
};

export const getProductURL = (product) => {
  if (
    product.provider === 'tiqets' ||
    product.provider === 'viator' ||
    product.provider === 'musement' ||
    product.provider === 'headout' ||
    product.provider === 'getyourguide'
  ) {
    const localePath = useLocalePath();
    return localePath(`/p/`) + `${Utility.sanitizeProductName(product.name)}-${product.code}`;
  } else {
    return product.url;
  }
};

export const openProduct = (product, customOpenURL = null) => {
  if (customOpenURL) {
    navigateTo(customOpenURL);
    return;
  }
  const mixpanel = useMixpanel();
  const productURL = getProductURL(product);
  const { proxy } = useScriptMetaPixel();

  try {
    mixpanel.track('Book Tour', {
      'Product ID': product.original_id,
      'Product Name': product.name,
      'Provider Name': product.provider,
      'Provider ID': product.providerID,
    });
    proxy.fbq('trackCustom', 'Book Tour');
  } catch (error) {
    console.error('Error tracking event:', error);
  }

  // Add product ID to recently viewed products cookie
  saveItemInCookie('RVProducts', product.id);

  window.open(productURL, '_blank');
};

/**
 * Formats time from seconds to a more readable string format.
 *
 * Formatting rules:
 * - For less than 1 hour: displays minutes only (e.g., "15 minutes", "30 minutes").
 * - For 1 hour or more: displays hours in decimal format if necessary (e.g., "1 hour", "2.5 hours").
 * - For 24 hours or more: displays days (e.g., "2 days").
 *
 * Examples:
 * - 900 seconds (15 minutes) => "15 minutes"
 * - 1800 seconds (30 minutes) => "30 minutes"
 * - 3600 seconds (1 hour) => "1 hour"
 * - 5400 seconds (1.5 hours) => "1.5 hours"
 * - 7200 seconds (2 hours) => "2 hours"
 * - 9000 seconds (2.5 hours) => "2.5 hours"
 * - 86400 seconds (1 day) => "1 day"
 * - 172800 seconds (2 days) => "2 days"
 *
 * @param {number} seconds - The time in seconds to format.
 * @returns {string} The formatted time string.
 */
export function formatHours(seconds, t) {
  if (!t) {
    throw new Error('t is required');
  }

  //TODO: check for 0 minutes duration
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const minOverHour = minutes % 60;

  if (hours < 1) {
    if (minutes === 1) return minutes + ' ' + t('minute'); // Singular minute
    return minutes + ' ' + t('minutes'); // minutes
  }
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    if (days >= 2) return days + ' ' + t('days');
    return days + ' ' + t('day'); // days singular
  }
  if (minOverHour === 0) {
    if (hours === 1) return hours + ' ' + t('hour'); // Singular hour
    return hours + ' ' + t('hours'); // hours
  }
  const decimalHours = (hours + minOverHour / 60).toFixed(1);
  return decimalHours + ' ' + t('hours'); // hours with decimal
}

export function extractQuestionsAndAnswers(content) {
  // Match pattern: <h2...><strong>Question</strong></h2>Answer content<h2>
  const questionRegex = /<h2[^>]*><strong>(.*?)<\/strong><\/h2>(.*?)(?=<h2|$)/gs;
  const qaArray = [];

  // Find all matches
  let matches = [...content.matchAll(questionRegex)];

  matches.forEach((match) => {
    const question = match[1].trim();
    const answer = match[2].trim(); // Keep HTML tags, just trim whitespace

    qaArray.push({
      question,
      answer,
    });
  });

  return qaArray;
}

// Function to save items in the cookie, cuz only __session cookie is persistent and isn't stripped by firebase
export function saveItemInCookie(itemKey, id) {
  if (!id) return;

  // Setting 5 years from now to expire
  const expires = new Date(new Date().getTime() + 5 * 365 * 24 * 60 * 60 * 1000);

  // Get the session cookie or an empty object if not set
  const sessionCookie = useCookie('__session', { expires });
  const cookieValue = sessionCookie.value || {};

  // Get the existing array or initialize an empty array
  const itemsArray = cookieValue[itemKey] || [];

  // Add the new item if it doesn't already exist
  if (!itemsArray.some((item) => item.id === id)) {
    itemsArray.unshift({ id, date: new Date() });
  }

  // Limit the array to 10 elements
  if (itemsArray.length > 10) itemsArray.pop();

  // Update the cookie while preserving other properties
  sessionCookie.value = { ...cookieValue, [itemKey]: itemsArray };
}

export function getRelativeTimeString(date) {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
    { label: 'second', seconds: 1 },
  ];

  for (let i = 0; i < intervals.length; i++) {
    const interval = intervals[i];
    const count = Math.floor(diffInSeconds / interval.seconds);
    if (count >= 1) {
      return count === 1 ? `1 ${interval.label} ago` : `${count} ${interval.label}s ago`;
    }
  }

  return 'just now';
}

export function formatDate(dateString) {
  const { t, d } = useI18n();
  const isoDate = new Date(dateString.toISOString() + 'T00:00:00Z').toISOString();
  return d(isoDate, 'short');
}

export function toSnakeCase(sentence) {
  return sentence
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .toLowerCase(); // Convert to lower case
}